'use client';

import React, { useMemo } from 'react';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetReferralVolumeTrendsQuery } from 'src/api/redux';
import { MedBarChart } from 'src/components/MedCharts/MedBarChart';
import type { BarChartGroup } from 'src/components/MedCharts/MedBarChart';

const CODE_SET_COLORS = [
  '#0d9488', // teal
  '#2563eb', // blue
  '#f59e0b', // amber
  '#7c3aed', // violet
  '#ea580c', // orange
  '#ec4899', // pink
  '#10b981', // emerald
  '#6366f1', // indigo
];

const ReferralVolumeTrends: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;

  const entityId = entityDetails?.id;
  const direction = widget?.direction;
  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    minYear && maxYear
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const codeSets = useMemo(() => {
    const sets =
      widget?.code_sets?.length ? widget.code_sets
      : widget?.code_set ? [widget.code_set]
      : [];
    const encs =
      widget?.encounters?.length ? widget.encounters
      : widget?.encounter ? [widget.encounter]
      : [];
    return [...sets, ...encs];
  }, [widget?.code_sets, widget?.code_set, widget?.encounters, widget?.encounter]);

  const q0 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[0]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[0] || !direction }
  );
  const q1 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[1]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[1] || !direction }
  );
  const q2 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[2]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[2] || !direction }
  );
  const q3 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[3]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[3] || !direction }
  );
  const q4 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[4]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[4] || !direction }
  );
  const q5 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[5]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[5] || !direction }
  );
  const q6 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[6]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[6] || !direction }
  );
  const q7 = useGetReferralVolumeTrendsQuery(
    {
      entityId: entityId || '',
      codeSetId: codeSets[7]?.id || 0,
      direction: direction || 'inbound',
      dateRange,
    },
    { skip: !entityId || !codeSets[7] || !direction }
  );

  const queries = [q0, q1, q2, q3, q4, q5, q6, q7];
  const isLoading = queries.some((q, i) => codeSets[i] && q.isLoading);

  const { groups, hasData } = useMemo(() => {
    const allGroups: BarChartGroup[] = [];
    const yearSet = new Set<string>();

    codeSets.forEach((cs, i) => {
      const data = queries[i]?.data;
      if (!data) return;
      Object.keys(data.claims_trends).forEach((y) => yearSet.add(y));
    });

    const years = Array.from(yearSet).sort();

    codeSets.forEach((cs, i) => {
      const data = queries[i]?.data;
      if (!data) return;

      allGroups.push({
        label: cs.label,
        color: CODE_SET_COLORS[i % CODE_SET_COLORS.length],
        data: years.map((y) => ({ x: y, y: data.claims_trends[y] ?? 0 })),
      });
    });

    return {
      groups: allGroups,
      hasData: allGroups.length > 0 && years.length > 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSets, q0.data, q1.data, q2.data, q3.data, q4.data, q5.data, q6.data, q7.data]);

  return (
    <Card
      className="p-4 flex flex-col gap-3 bg-white h-full"
      data-testid="referral-volume-trends"
      role="region"
      aria-label={widget?.label ?? 'Referral Volume Trends'}
    >
      <WidgetHeader fallbackTitle="Referral Volume Trends" />
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading || widgetConfig?.isLoading ? (
          <WidgetLoading data-testid="referral-volume-trends-loading" />
        ) : !hasData ? (
          <WidgetEmpty data-testid="referral-volume-trends-empty" variant="full" message="No trend data available" />
        ) : (
          <div
            className="w-full"
            data-testid="referral-volume-trends-chart"
          >
            <MedBarChart
              groups={groups}
              showPercentChange={widget?.show_percent_change !== false}
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default ReferralVolumeTrends;
